import React from 'react';
import { Activity, CheckCircle, Clock, Shield, Zap, TrendingUp } from 'lucide-react';

const Status = () => {
  const timeline = [
    {
      period: "October 2025",
      title: "Project Initiation",
      items: [
        "Initial concept and architecture planning",
        "Desktop-first approach chosen over web",
        "Core AI interaction experiments"
      ]
    },
    {
      period: "Late 2025",
      title: "Foundation Phase",
      items: [
        "Supabase authentication integration",
        "Google OAuth login system",
        "Secure backend architecture",
        "Early voice command prototypes"
      ]
    },
    {
      period: "Early 2026",
      title: "Core Features",
      items: [
        "Stable desktop app builds",
        "Voice-to-command pipeline",
        "AI processing via secured backend",
        "Usage tracking and account-based limits",
        "Preparation for payment and subscription systems"
      ]
    }
  ];

  const focusAreas = [
    { icon: Zap, text: "Improving voice recognition accuracy" },
    { icon: Clock, text: "Reducing AI response latency" },
    { icon: Shield, text: "Strengthening security & API protection" },
    { icon: TrendingUp, text: "Expanding automation capabilities" },
    { icon: Activity, text: "Scaling backend infrastructure" }
  ];

  const plannedImprovements = [
    "Advanced voice workflows",
    "Custom command creation",
    "Smarter context-aware AI responses",
    "Cross-device user management",
    "Performance optimizations"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-full mb-6 animate-pulse">
            <Activity className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Status Page
          </h1>
          <div className="inline-flex items-center space-x-2 px-6 py-3 bg-green-500/20 border border-green-500/30 rounded-full">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-500 font-semibold">Current Status: Active Development & Stable Core</span>
          </div>
          <p className="text-xl text-gray-400 mt-4 max-w-3xl mx-auto">
            Twe Voice Assistant is under continuous active development, with a focus on reliability, security, and long-term scalability.
          </p>
        </div>

        {/* Development Timeline */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Development Timeline
          </h2>
          <div className="space-y-8">
            {timeline.map((phase, index) => (
              <div
                key={index}
                className="bg-zinc-900/50 border border-white/5 rounded-xl p-8 hover:border-orange-500/30 transition-all duration-300"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-orange-500 font-bold">{index + 1}</span>
                  </div>
                  <div>
                    <p className="text-orange-500 font-semibold">{phase.period}</p>
                    <h3 className="text-2xl font-bold text-white">{phase.title}</h3>
                  </div>
                </div>
                <ul className="space-y-2 ml-16">
                  {phase.items.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Current Focus Areas */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Current Focus Areas
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {focusAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <div
                  key={index}
                  className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300 hover:transform hover:scale-105"
                >
                  <Icon className="w-8 h-8 text-orange-500 mb-3" />
                  <p className="text-gray-300">{area.text}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Security & Reliability */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Security & Reliability
          </h2>
          <div className="bg-gradient-to-br from-zinc-900 to-black border border-green-500/30 rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-6">
              {[
                "Backend services protected from client-side exposure",
                "Authentication handled via trusted providers",
                "Continuous monitoring for errors and abuse",
                "Gradual rollout of advanced features"
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Shield className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Planned Improvements */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Planned Improvements
          </h2>
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-8">
            <div className="grid md:grid-cols-2 gap-4">
              {plannedImprovements.map((improvement, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <span className="text-gray-300">{improvement}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Transparency Commitment */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Transparency Commitment
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Twe is built with transparency in mind. If services are degraded or features are temporarily unavailable, updates will be communicated clearly on this page.
          </p>
        </div>

        {/* Contact */}
        <div className="mt-12 text-center">
          <p className="text-gray-400">
            Questions about status or need support?{' '}
            <a
              href="mailto:contact@twe-assistant.com"
              className="text-orange-500 hover:text-orange-400 transition-colors duration-200"
            >
              contact@twe-assistant.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Status;